import { useMemo } from "react";
import { useTranslations } from "next-intl";
import { DataField, Stack } from "@leabankab/component-library";
import { useCurrency, useOfferIntegrityWarning } from "@/hooks";
import { AMOUNT_PLACEHOLDER, formatRate, toAmount } from "@/shared/amount";
import { formatLoanType } from "@/shared/formaters";
import { useMonthsToYears } from "@/shared/use-months-to-years";
import { useConstantOptions } from "../../../customer-information/hooks/use-constant-options";
import { LoanSummaryPageContext } from "../../types";

type Props = {
	context: LoanSummaryPageContext;
	hideDebts?: boolean;
};

export const AboutTheLoan = ({ context, hideDebts }: Props) => {
	const t = useTranslations("pages.SummaryPage");
	const { offer, payouts } = context;
	const {
		startupFee,
		interestRate,
		terms,
		termFee,
		effectiveInterestRate,
		monthlyCost,
		finalLoanAmount,
	} = offer;
	const { format } = useCurrency();
	const formatDuration = useMonthsToYears();
	const formatPerMonth = (...args: Parameters<typeof format>) =>
		toAmount(args[0]) === null
			? AMOUNT_PLACEHOLDER
			: t("loan.content.perMonthValue", { amount: format(...args) });

	useOfferIntegrityWarning(offer, "summary-page");

	const loanTypeOptions = useConstantOptions("loan-types");
	const refinanceDebtsArray = payouts?.map((payout) => ({
		type: formatLoanType(payout.description, loanTypeOptions),
		amount: payout.amount,
		bank: payout.bank,
	}));

	const sumDebts = useMemo(
		() =>
			refinanceDebtsArray
				? refinanceDebtsArray
						.map((debt) => debt.amount)
						.reduce((acc, amount) => acc + amount, 0)
				: 0,
		[refinanceDebtsArray]
	);
	return (
		<Stack>
			<DataField
				label={t("loan.content.totalLoanAmount")}
				value={format(finalLoanAmount)}
			/>
			{hideDebts && (
				<DataField
					label={t("loan.content.sumDebts")}
					value={format(sumDebts)}
				/>
			)}
			<DataField
				label={t("loan.content.startupFee")}
				value={format(startupFee)}
			/>
			<DataField
				label={t("loan.content.termFee")}
				value={formatPerMonth(termFee)}
			/>
			<DataField
				label={t("loan.content.interestRate")}
				value={formatRate(interestRate)}
			/>
			<DataField
				label={t("loan.content.effectiveInterestRate")}
				value={formatRate(effectiveInterestRate)}
			/>
			<DataField
				label={t("loan.content.terms")}
				value={formatDuration(terms)}
			/>
			<DataField
				label={t("loan.content.monthlyCost")}
				value={formatPerMonth(monthlyCost)}
				divider={false}
			/>
		</Stack>
	);
};
